/**
 * <sticky-atc> custom element
 *
 * Behavior:
 *   1. Observes the main product form's submit button via IntersectionObserver.
 *      When that button scrolls out of view, the sticky bar reveals.
 *   2. Clicking the bar's button triggers the main add-to-cart submit.
 *   3. Subscribes to Dawn's `variant:change` PubSub event (Dawn 14+) to sync
 *      price, image, and button availability. Falls back to listening for
 *      `change` events on the product form when PubSub is unavailable.
 *
 * Designed for Dawn 15.x with True Classic-style product page customizations.
 */
if (!customElements.get('sticky-atc')) {
  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('[data-sticky-atc-trigger]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.mediaImg = this.querySelector('.sticky-atc__media img');
      this.watchSelector = this.getAttribute('data-watch-selector') || '.product-form__buttons';
      this.offset = parseInt(this.getAttribute('data-offset') || '80', 10);
      this._visible = false;
      this._unsubscribers = [];
    }

    connectedCallback() {
      this.setupIntersectionObserver();
      this.setupButtonProxy();
      this.setupVariantSync();
      // Initial sync in case the merchant set up the section with a non-default variant.
      this.syncFromDom();
    }

    disconnectedCallback() {
      if (this._intersectionObserver) this._intersectionObserver.disconnect();
      if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
      this._unsubscribers.forEach((fn) => { try { fn(); } catch (e) {} });
    }

    /* ---------------- Visibility (reveal on scroll past CTA) ---------------- */

    setupIntersectionObserver() {
      const start = (attempt) => {
        const watchEl = document.querySelector(this.watchSelector);
        if (!watchEl) {
          if (attempt < 8) {
            // The product-info section may not be rendered yet on first paint.
            setTimeout(() => start(attempt + 1), 300);
          }
          return;
        }
        this._watchEl = watchEl;
        this._intersectionObserver = new IntersectionObserver(
          () => this.checkVisibility(),
          { threshold: 0, rootMargin: `0px 0px ${this.offset}px 0px` }
        );
        this._intersectionObserver.observe(watchEl);

        // Safety-net scroll handler — IO can miss when content height changes
        // (e.g. variant swap shrinks the section).
        this._scrollHandler = () => this.checkVisibility();
        window.addEventListener('scroll', this._scrollHandler, { passive: true });
        window.addEventListener('resize', this._scrollHandler, { passive: true });
        this.checkVisibility();
      };
      start(0);
    }

    checkVisibility() {
      if (!this._watchEl) return;
      // Re-query in case Dawn replaced the product-info section during a variant swap.
      if (!this._watchEl.isConnected) {
        const fresh = document.querySelector(this.watchSelector);
        if (fresh) this._watchEl = fresh;
        else return;
      }
      const rect = this._watchEl.getBoundingClientRect();
      const isPast = rect.bottom < -this.offset;
      this.setVisible(isPast);
    }

    setVisible(visible) {
      if (visible === this._visible) return;
      this._visible = visible;
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    /* ---------------- Button proxy ---------------- */

    setupButtonProxy() {
      if (!this.button) return;
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        // Re-query each click since Dawn may have replaced the main form.
        const mainBtn = document.querySelector(
          'product-form .product-form__submit, .product-form__buttons .product-form__submit'
        );
        if (mainBtn) mainBtn.click();
      });
    }

    /* ---------------- Variant sync ---------------- */

    setupVariantSync() {
      // Dawn 14+ exposes a PubSub event for variant changes. Use it when present.
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
        const unsub = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event && event.data && event.data.variant;
          if (variant) {
            this.syncFromVariant(variant);
          } else {
            this.syncFromDom();
          }
        });
        if (typeof unsub === 'function') this._unsubscribers.push(unsub);
        return;
      }

      // Fallback: listen for changes anywhere on the document. We delegate to
      // catch radio toggles inside variant-selects without needing a stable form ref.
      const handler = () => {
        // Defer one frame so Dawn has time to update data-selected-variant.
        requestAnimationFrame(() => this.syncFromDom());
      };
      document.addEventListener('change', handler);
      this._unsubscribers.push(() => document.removeEventListener('change', handler));
    }

    syncFromDom() {
      const script = document.querySelector('product-info script[data-selected-variant]')
        || document.querySelector('script[data-selected-variant]');
      if (!script) return;
      try {
        const variant = JSON.parse(script.textContent);
        if (variant) this.syncFromVariant(variant);
      } catch (e) { /* ignore */ }
    }

    syncFromVariant(variant) {
      // Update price using formatted DOM values (handles currency / discounts correctly).
      if (this.priceEl) {
        const container = document.querySelector('.product__info-container');
        if (container) {
          const saleEl = container.querySelector('.price__sale .price-item--sale');
          const regularEl = container.querySelector('.price__regular .price-item--regular, .price-item--regular');
          const compareEl = container.querySelector('.price__sale .price-item--last, .price-item--last');
          const primary = (saleEl && saleEl.textContent.trim()) || (regularEl && regularEl.textContent.trim());
          if (primary) {
            this.priceEl.innerHTML = '';
            this.priceEl.appendChild(document.createTextNode(primary));
            if (variant.compare_at_price && variant.compare_at_price > variant.price && compareEl && compareEl.textContent.trim()) {
              const compare = document.createElement('span');
              compare.className = 'sticky-atc__price-compare';
              compare.textContent = compareEl.textContent.trim();
              this.priceEl.appendChild(document.createTextNode(' '));
              this.priceEl.appendChild(compare);
            }
          }
        }
      }

      // Availability
      if (this.button) {
        this.button.disabled = !variant.available;
      }

      // Featured image
      if (this.mediaImg && variant.featured_media && variant.featured_media.preview_image) {
        const previewSrc = variant.featured_media.preview_image.src;
        if (previewSrc) {
          // Build a 200px URL (Shopify CDN supports ?width=N)
          const sep = previewSrc.indexOf('?') > -1 ? '&' : '?';
          const wantedSrc = previewSrc + sep + 'width=200';
          // Only swap if the underlying file actually changed (compare ignoring query string).
          const currentBase = (this.mediaImg.src || '').split('?')[0];
          const wantedBase = previewSrc.split('?')[0];
          if (currentBase !== wantedBase) {
            this.mediaImg.src = wantedSrc;
          }
        }
      }
    }
  }
  customElements.define('sticky-atc', StickyAtc);
}
